import { makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import classnames from 'classnames';
import _flatten from 'lodash/flatten';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import {
  GATriggerType,
  ImagemapTemplateType,
  NodeType,
} from '../../../app/types';
import { randomText, validateMessages } from '../../../app/utils';
import IconAddCircle from '../../../assets/images/Icons/IconAddCircle';
import {
  addMessage,
  changeMessageCheckCondition,
  deleteMessage,
  downMessage,
  setNodeData,
  upMessage,
} from '../../../features/mainSlice';
import ConfirmCardTemplate from '../../shared/templates/ConfirmCardTemplate';
import FileTemplate from '../../shared/templates/FileTemplate';
import ImageCardTemplate from '../../shared/templates/ImageCardTemplate';
import ImagemapTemplate from '../../shared/templates/ImagemapTemplate';
import OptionalCardTemplate from '../../shared/templates/OptionalCardTemplate';
import TextTemplate from '../../shared/templates/TextTemplate';
import WaListTemplate from '../../shared/templates/WaListTemplate';
import WhatsappTemplate from '../../shared/templates/WhatsappTemplate';

const useStyles = makeStyles(
  {
    inputTitle: {
      fontSize: '15px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      marginBottom: 12,
      marginTop: '36px',
    },
    message: {
      width: '100%',
    },
    'message-small': {
      width: '100%',
    },
    icon: {
      display: 'inline-flex',
      alignItems: 'center',
      color: color.$2,
      fontSize: 15,
      lineHeight: 1,
      cursor: 'pointer',
      '&:hover': {
        opacity: '0.6',
      },
      '& > svg': {
        marginRight: 4,
      },
    },
    errorMessage: {
      color: color.$17,
    },
  },
  'Messages',
);

const Messages = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    data: maData,
    selectedNode,
    isErrorNodeIds,
  } = useSelector(state => state.ma);
  const { enabledEC } = useSelector(store => store.organization);
  const { messages } = selectedNode?.data;
  const { platform: platformType, nodes = [], edges = [] } = maData || {};
  const isLessVW1280 = useMediaQuery('(max-width:1280px)');
  const messageLength = messages.length;
  const isError = !!~isErrorNodeIds.indexOf(selectedNode?.id);

  const sourceNodeId = useMemo(
    () => (edges.find(e => e?.target === selectedNode?.id) || {})?.source,
    [selectedNode],
  );

  const sourceNode = useMemo(
    () => nodes.find(n => n?.id === sourceNodeId),
    [sourceNodeId],
  );

  const groupNode = useMemo(
    () => nodes.find(n => n?.id === sourceNode?.data?.groupId),
    [sourceNode],
  );

  // 當上方SourceNode是“GA事件”或是“觸發條件的GA事件”時發送訊息需顯示“自動帶入商品資訊”選項
  // GA事件 -> 完成加入購物車、未完成結帳才有“自動帶入商品資訊”選項
  const isNeedAutoTrackProduct =
    enabledEC === 'cyberbiz' &&
    ((sourceNode?.type === NodeType?.TRIGGER &&
      sourceNode?.data?.type === GATriggerType.GAADDTOCART) ||
      (sourceNode?.type === NodeType?.GACONDITION &&
        sourceNode?.data?.type === 'trigger' &&
        groupNode?.data?.triggerType === GATriggerType.GAADDTOCART) ||
      (sourceNode?.type === NodeType?.GACONDITION &&
        sourceNode?.data?.type === 'n-trigger' &&
        groupNode?.data?.triggerType === GATriggerType.GAPURCHASE));

  const checkIsNeedChangeCondition = (oldMessage, newMessage) => {
    const oldMessageAllButtons = _flatten(
      (oldMessage?.data?.elements || []).map(e => e?.buttons),
    );
    const newMessageAllButtons = _flatten(
      (newMessage?.data?.elements || []).map(e => e?.buttons),
    );
    const elementsMatch =
      (oldMessage?.data?.elements || []).length ===
      (newMessage?.data?.elements || []).length;
    const buttonsMatch =
      (oldMessageAllButtons || []).length ===
      (newMessageAllButtons || []).length;
    const buttonsTypeMatch = oldMessageAllButtons.every(
      (button, buttonIndex) =>
        button?.type === newMessageAllButtons[buttonIndex]?.type,
    );

    return !(elementsMatch && buttonsMatch && buttonsTypeMatch);
  };

  function handleChangeMessage(data, dataIndex) {
    const callback = () => {
      dispatch(
        setNodeData({
          messages: messages.map((msg, index) => {
            if (index === dataIndex) {
              return {
                ...msg,
                ...data,
                index,
              };
            }
            return msg;
          }),
        }),
      );
    };

    if (
      data?.contentType === 'application/x-template' &&
      checkIsNeedChangeCondition(messages[dataIndex], data)
    ) {
      dispatch(changeMessageCheckCondition(selectedNode?.id, callback));
    } else {
      callback();
    }
  }

  function onChangeType(type, index) {
    dispatch(
      changeMessageCheckCondition(selectedNode?.id, () => {
        dispatch(
          setNodeData({
            messages: messages.map((msg, messageIndex) => {
              if (index === messageIndex) {
                switch (type) {
                  case 'waList':
                    return {
                      contentType: 'application/x-template',
                      template: randomText(),
                      data: {
                        templateType: 'waList',
                        elements: [
                          {
                            title: '',
                            subtitle: '',
                            buttons: [
                              {
                                type: 'postback',
                                title: '',
                                subtitle: '',
                                sectionIndex: 0,
                              },
                            ],
                            listButtonText: '',
                            sections: [],
                          },
                        ],
                      },
                      index,
                    };
                  case 'whatsappTemplate':
                    return {
                      waTemplateId: '',
                      index,
                    };
                  case 'text':
                    return {
                      contentType: 'text/plain',
                      data: {
                        content: '',
                      },
                      index,
                    };
                  case 'file':
                    return {
                      contentType: 'application/x-image',
                      data: {
                        name: '',
                        contentType: '',
                        url: '',
                        previewUrl: null,
                      },
                      index,
                    };
                  case 'card':
                    return {
                      contentType: 'application/x-template',
                      template: randomText(),
                      data: {
                        templateType: 'card',
                        elements: [
                          {
                            goodsURL: '',
                            imageType: 'upload',
                            imageUrl: '',
                            title: '',
                            subtitle: '',
                            buttons: [
                              {
                                type: 'postback',
                                title: '',
                                data: '',
                                tags: [],
                              },
                            ],
                          },
                        ],
                      },
                      index,
                    };
                  case 'confirm':
                    return {
                      contentType: 'application/x-template',
                      template: randomText(),
                      data: {
                        templateType: 'confirm',
                        elements: [
                          {
                            title: '',
                            buttons: [
                              {
                                type: 'postback',
                                title: '',
                                data: '',
                                tags: [],
                              },
                              {
                                type: 'postback',
                                title: '',
                                data: '',
                                tags: [],
                              },
                            ],
                          },
                        ],
                      },
                      index,
                    };
                  case 'image':
                    return {
                      contentType: 'application/x-template',
                      template: randomText(),
                      data: {
                        templateType: 'image',
                        elements: [
                          {
                            goodsURL: '',
                            imageType: 'upload',
                            imageUrl: '',
                            buttons: [
                              {
                                type: 'postback',
                                title: '',
                                data: '',
                                tags: [],
                              },
                            ],
                          },
                        ],
                      },
                      index,
                    };
                  case 'imagemap':
                    return {
                      contentType: 'application/x-template',
                      template: randomText(),
                      data: {
                        templateType: 'imagemap',
                        elements: [
                          {
                            name: '',
                            imageUrl: '',
                            size: {
                              width: 1040,
                              height: 1040,
                            },
                            aspectRatio: '1:1',
                            messageTemplateType: 'ImagemapTemplate1',
                            buttons: ImagemapTemplateType.ImagemapTemplate1,
                          },
                        ],
                      },
                      index,
                    };
                  default:
                    return null;
                }
              }
              return msg;
            }),
          }),
        );
      }),
    );
  }

  function handleAddMessage() {
    dispatch(addMessage());
  }

  function handleDeleteMessage(index) {
    dispatch(
      changeMessageCheckCondition(selectedNode?.id, () => {
        dispatch(deleteMessage(index));
      }),
    );
  }

  function handleUpMessage(index) {
    dispatch(upMessage(index));
  }

  function handleDownMessage(index) {
    dispatch(downMessage(index));
  }

  const messagesComponents = useMemo(
    () =>
      messages.map((temp, index) => {
        if (temp?.waTemplateId !== undefined) {
          return (
            <WhatsappTemplate
              key={`template-${temp.index}`}
              template={temp}
              onChangeType={type => onChangeType(type, index)}
              onChange={data => handleChangeMessage(data, index)}
              onDelete={handleDeleteMessage}
              platformType={platformType}
              onUp={handleUpMessage}
              onDown={handleDownMessage}
              messageLength={messageLength}
              messageIndex={temp?.index ?? index}
              isError={isError}
            />
          );
        }

        switch (temp.contentType) {
          case 'text/plain':
            return (
              <TextTemplate
                key={`template-${temp.index}`}
                template={temp}
                onChangeType={type => onChangeType(type, index)}
                onChange={data => handleChangeMessage(data, index)}
                onDelete={handleDeleteMessage}
                platformType={platformType}
                onUp={handleUpMessage}
                onDown={handleDownMessage}
                messageLength={messageLength}
                isError={isError && !validateMessages([temp])}
              />
            );
          case 'application/x-image':
          case 'application/x-audio':
          case 'application/x-video':
          case 'application/x-file':
            return (
              <FileTemplate
                key={`template-${temp.index}`}
                template={temp}
                onChangeType={type => onChangeType(type, index)}
                onChange={data => handleChangeMessage(data, index)}
                onDelete={handleDeleteMessage}
                platformType={platformType}
                onUp={handleUpMessage}
                onDown={handleDownMessage}
                messageLength={messageLength}
                isError={isError && !validateMessages([temp])}
              />
            );
          default:
        }
        switch (temp.templateType || temp.data.templateType) {
          case 'card':
            return (
              <OptionalCardTemplate
                key={`template-${temp.index}`}
                template={temp}
                onChangeType={type => onChangeType(type, index)}
                onChange={data => handleChangeMessage(data, index)}
                onDelete={handleDeleteMessage}
                platformType={platformType}
                onUp={handleUpMessage}
                onDown={handleDownMessage}
                messageLength={messageLength}
                messageIndex={temp.index}
                isError={isError && !validateMessages([temp], platformType)}
                isShowAutoTrackProduct={isNeedAutoTrackProduct}
              />
            );
          case 'confirm':
            return (
              <ConfirmCardTemplate
                key={`template-${temp.index}`}
                template={temp}
                onChangeType={type => onChangeType(type, index)}
                onChange={data => handleChangeMessage(data, index)}
                onDelete={handleDeleteMessage}
                platformType={platformType}
                onUp={handleUpMessage}
                onDown={handleDownMessage}
                messageLength={messageLength}
                messageIndex={temp.index}
                isError={isError && !validateMessages([temp], platformType)}
              />
            );
          case 'image':
            return (
              <ImageCardTemplate
                key={`template-${temp.index}`}
                template={temp}
                onChangeType={type => onChangeType(type, index)}
                onChange={data => handleChangeMessage(data, index)}
                onDelete={handleDeleteMessage}
                platformType={platformType}
                onUp={handleUpMessage}
                onDown={handleDownMessage}
                messageLength={messageLength}
                messageIndex={temp.index}
                isError={isError && !validateMessages([temp])}
              />
            );
          case 'imagemap':
            return (
              <ImagemapTemplate
                key={`template-${temp.index}`}
                template={temp}
                onChangeType={type => onChangeType(type, index)}
                onChange={data => handleChangeMessage(data, index)}
                onDelete={handleDeleteMessage}
                platformType={platformType}
                onUp={handleUpMessage}
                onDown={handleDownMessage}
                messageLength={messageLength}
                isError={isError && !validateMessages([temp])}
              />
            );
          case 'waList':
            return (
              <WaListTemplate
                key={`template-${temp.index}`}
                template={temp}
                onChangeType={type => onChangeType(type, index)}
                onChange={data => handleChangeMessage(data, index)}
                onDelete={handleDeleteMessage}
                platformType={platformType}
                onUp={handleUpMessage}
                onDown={handleDownMessage}
                messageLength={messageLength}
                messageIndex={temp.index}
                isError={isError && !validateMessages([temp])}
              />
            );
          default:
        }
        return null;
      }),
    [messages, isErrorNodeIds],
  );

  return (
    <>
      <div className={classes.inputTitle}>{t('messages.title')}</div>
      <div
        className={classnames(classes.message, {
          [classes['message-small']]: isLessVW1280,
        })}
      >
        {messagesComponents}
      </div>
      {messages.length < 5 && (
        <div className={classes.icon} onClick={handleAddMessage}>
          <IconAddCircle id="icon-add-message-type" width={20} height={20} />
          {t('messages.add')}
        </div>
      )}
    </>
  );
};

export default Messages;
